/** Cleaning / normalising BOM text fields (original cleanPos, cleanDesc, unitOf, fabCatOf, trimCatOf). */
import type { History } from "./types.ts";

/** Placement: drop NULL / openpyxl reprs / "0" / plain "fabric", strip a leading "41," style index and a trailing backtick. */
export function cleanPos(v: unknown): string {
  const s = String(v == null ? "" : v).trim();
  if (!s || s === "NULL" || s.indexOf("<openpyxl") === 0 || s === "0" || /^fabric$/i.test(s)) return "";
  return s.replace(/^\d+\s*[,，、.]\s*/, "").replace(/`$/, "").trim();
}

/** Description: NULL/None/nan → "", drop a trailing semicolon, collapse whitespace. */
export function cleanDesc(v: unknown): string {
  const s = String(v == null ? "" : v).trim();
  if (/^(NULL|None|nan)$/i.test(s)) return "";
  return s.replace(/;\s*$/, "").replace(/\s+/g, " ").trim();
}

/** BOM unit → sheet unit (m / yd / kg / sqft / pcs). */
export function unitOf(u: unknown): string {
  const s = String(u || "").toUpperCase();
  if (/^M$|MTR|METER/.test(s)) return "m";
  if (/YD|YARD/.test(s)) return "yd";
  if (/KG/.test(s)) return "kg";
  if (/SQ|FT/.test(s)) return "sqft";
  return "pcs";
}

/**
 * Fabric category from a composition text: the fibre with the highest % that maps to a "Woven <fibre>"
 * category of the history (exact name, or the fibre word starts with the category's fibre).
 */
export function fabCatOf(comp: unknown, hist: Pick<History, "fabGlobal">): string {
  const cats = Object.keys(hist.fabGlobal || {});
  let best = "";
  let bp = -1;
  String(comp || "").replace(/(\d+(?:\.\d+)?)\s*%\s*([A-Za-z]+)/g, (_m, pcS: string, fb: string) => {
    const pc = +pcS;
    if (pc > bp) {
      const f = fb.toLowerCase();
      const k = cats.filter((x) => x.toLowerCase() === "woven " + f || f.indexOf(x.slice(6).toLowerCase()) === 0)[0];
      if (k) { best = k; bp = pc; }
    }
    return "";
  });
  return best;
}

const tcatCache = new WeakMap<object, Record<string, string>>();
/** Trim category, case-insensitive against the history's trim categories ("" if none). */
export function trimCatOf(t: unknown, hist: Pick<History, "trimGlobal">): string {
  const g = hist.trimGlobal || {};
  let m = tcatCache.get(g);
  if (!m) {
    m = {};
    for (const k of Object.keys(g)) m[k.toUpperCase()] = k;
    tcatCache.set(g, m);
  }
  return m[String(t || "").toUpperCase()] || "";
}

/** Thumbnail URL → large image (w_240 → w_900). */
export function bigImg(u: unknown): string {
  return String(u || "").replace(/resize,w_\d+/, "resize,w_900");
}
